import { sendToApi } from 'api/client'
import { Filters as ByDescriptionFilters } from './useVisualTemplatesByDescription'
import { VisualElement } from 'api/clients/VisualElementClient'
import qs from 'qs'
import { SUCCESS_RESPONSE_TYPE } from 'api/constants'
import { UseQueryOptions, useQuery } from 'react-query'
import { fetchVisualElements } from './useVisualTemplates'
import { useRootStore } from 'stores/RootStore'
import shuffle from 'lodash/shuffle'

interface Props extends ByDescriptionFilters {
  byDescriptionItems?: number
  basicLayoutItems?: number
  additional_seed?: string
}

interface Pagination {
  items?: number
}

interface Data {
  visual_elements: VisualElement[]
}

export const fetchByDescription = async (params: ByDescriptionFilters & Pagination) => {
  const { description, ...rest } = params
  const truncatedDescription = description.slice(0, 1000)
  const paramsWithDescription = {
    ...rest,
    description: truncatedDescription,
  }

  const queryString = qs.stringify(paramsWithDescription, { arrayFormat: 'brackets' })
  const res = await sendToApi<{ visual_elements: VisualElement[] }>(
    `/visual_elements/find_by_description?${queryString}`,
  )
  if (res.type === SUCCESS_RESPONSE_TYPE) {
    return res.data
  }
  throw new Error()
}

export const fetchBasicAndByDescription = async (params: Props) => {
  const {
    width,
    height,
    description,
    has_replaceable,
    replaceable_has_background,
    byDescriptionItems = 5,
    basicLayoutItems = 2,
    type,
    additional_seed,
  } = params

  const templatesByDescription =
    byDescriptionItems === 0
      ? []
      : (
          await fetchByDescription({
            width,
            height,
            description,
            has_replaceable,
            replaceable_has_background,
            items: byDescriptionItems,
            type,
          })
        ).visual_elements
  const basicTemplates =
    basicLayoutItems === 0
      ? []
      : (
          await fetchVisualElements({
            width,
            height,
            items: basicLayoutItems,
            basic_layout: true,
            order_by: 'random',
            has_replaceable,
            replaceable_has_background,
            type,
            additional_seed,
          })
        ).data.visual_elements

  const count = basicLayoutItems + byDescriptionItems
  const templates = shuffle([...basicTemplates, ...templatesByDescription].slice(0, count))

  return { visual_elements: templates }
}

/**
 * This hook returns a number of templates by description along with a number
 * of basic templates. If present, basic templates always take place of templates
 * by description. So if you want to return 4 items, you must always pass this
 * total count as `byDescriptionItems` to ensure that enough items are returned
 * in case basic layouts are not available.
 */
export const useBasicVisualElementsAndVisualElementsByDescription = (
  props: Props,
  options?: Partial<UseQueryOptions<Data>>,
) => {
  const { width, height, description, has_replaceable, replaceable_has_background } = props
  const { visualElements } = useRootStore()
  const queryKey = [
    'basicLayoutVisualElementsAndVisualElementsByDescription',
    { width, height, description, has_replaceable, replaceable_has_background },
  ]

  return useQuery<Data>(
    queryKey,
    async () => {
      const { visual_elements } = await fetchBasicAndByDescription(props)
      visualElements.bulkAdd(visual_elements)
      return { visual_elements }
    },
    {
      ...options,
      retry: false,
      refetchOnWindowFocus: false,
    },
  )
}
